/**
 * SSE client for `POST /api/v1/chat/message` (fetch + reader: EventSource cannot send the Bearer header).
 *
 * Server contract (B1, «Final HTTP contract»): non-2xx answers are plain JSON `{ error }` before any
 * stream; a 200 stream carries `event: conversation` (first message only), default `data: {delta}`
 * frames, then exactly one `done` or `error` — or nothing when the connection dropped.
 */

export type SseEvent = { event: string; data: unknown };

/** Parse one SSE block (lines split by \n, blocks by \n\n). Returns null if no data. */
export function parseSseBlock(block: string): SseEvent | null {
  let event = 'message';
  let dataRaw = '';
  for (const line of block.split('\n')) {
    if (line.startsWith('event:')) event = line.slice(6).trim() || 'message';
    else if (line.startsWith('data:')) dataRaw += line.slice(5).trim();
  }
  if (!dataRaw) return null;
  try {
    return { event, data: JSON.parse(dataRaw) };
  } catch {
    return null;
  }
}

export type ChatErrorCode =
  | 'insufficient_stars' | 'chart_gone' | 'chart_not_ready' | 'stream_error' // SSE error events
  | 'rate' | 'in_flight' | 'message_too_long' | 'chart_not_found' | 'not_found' | 'bad_request' // HTTP before the stream
  | 'chat_disabled' | 'course_locked' | 'quote_changed' | 'answer_changed' | 'version_conflict' | 'chat_unavailable'
  | 'network'; // fetch rejected / stream ended with no done|error / no body

const SSE_ERRORS: ReadonlySet<string> = new Set(['insufficient_stars', 'chart_gone', 'chart_not_ready', 'stream_error']);
const HTTP_ERRORS: ReadonlySet<string> = new Set([
  'insufficient_stars',
  'chat_disabled', 'course_locked', 'quote_changed', 'answer_changed', 'version_conflict', 'chat_unavailable',
  'rate', 'in_flight', 'message_too_long', 'chart_not_found', 'not_found', 'chart_not_ready', 'bad_request',
]);

export interface ChatDone {
  messageId: string;
  starsSpent: number;
  conversationId: string;
}

export interface StreamChatParams {
  base: string;
  path?: string;
  jwt: string;
  body: unknown;
  signal?: AbortSignal;
  onConversation: (conversationId: string) => void;
  onDelta: (s: string) => void;
  onDone: (d: ChatDone) => void;
  onError: (code: ChatErrorCode) => void;
}

const isAbort = (err: unknown, signal?: AbortSignal): boolean =>
  !!signal?.aborted || (err instanceof Error && err.name === 'AbortError') ||
  (typeof DOMException !== 'undefined' && err instanceof DOMException && err.name === 'AbortError');

function asRecord(v: unknown): Record<string, unknown> {
  return v && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : {};
}

async function httpErrorCode(res: Response): Promise<ChatErrorCode> {
  try {
    const code = asRecord(await res.json()).error;
    if (typeof code === 'string' && HTTP_ERRORS.has(code)) return code as ChatErrorCode;
  } catch {
    // not JSON
  }
  return 'stream_error';
}

/**
 * Sends one chat message and streams the answer. Never throws: every call ends in exactly one
 * `onError` or one `onDone` — except an abort, which ends silently.
 */
export async function streamChat(p: StreamChatParams): Promise<void> {
  let res: Response;
  try {
    res = await fetch(`${p.base}${p.path ?? '/api/v1/chat/message'}`, {
      method: 'POST',
      headers: { 'content-type': 'application/json', authorization: `Bearer ${p.jwt}` },
      body: JSON.stringify(p.body),
      signal: p.signal,
    });
  } catch (err) {
    if (!isAbort(err, p.signal)) p.onError('network');
    return;
  }
  if (!res.ok) {
    const code = await httpErrorCode(res);
    if (!p.signal?.aborted) p.onError(code);
    return;
  }
  // Course open can reuse a conversation or return its free hint without a stream.
  if (res.headers.get('content-type')?.includes('application/json')) {
    try {
      const data = asRecord(await res.json());
      const id = typeof data.conversationId === 'string' ? data.conversationId : '';
      if (id) p.onConversation(id);
      if (typeof data.hint === 'string') p.onDelta(data.hint);
      p.onDone({ conversationId: id, messageId: '', starsSpent: 0 });
    } catch { if (!p.signal?.aborted) p.onError('network'); }
    return;
  }
  if (!res.body) {
    p.onError('network');
    return;
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buf = '';
  let finished = false;

  /** Handles one block; returns true when it was terminal. */
  const handle = (block: string): boolean => {
    const ev = parseSseBlock(block);
    if (!ev) return false;
    const data = asRecord(ev.data);
    switch (ev.event) {
      case 'conversation':
        if (typeof data.conversationId === 'string') p.onConversation(data.conversationId);
        return false;
      case 'message':
        if (typeof data.delta === 'string') p.onDelta(data.delta);
        return false;
      case 'done':
        p.onDone({
          messageId: String(data.messageId ?? ''),
          starsSpent: typeof data.starsSpent === 'number' ? data.starsSpent : 0,
          conversationId: String(data.conversationId ?? ''),
        });
        return true;
      case 'error': {
        const code = typeof data.error === 'string' && SSE_ERRORS.has(data.error) ? data.error : 'stream_error';
        p.onError(code as ChatErrorCode);
        return true;
      }
      default:
        return false; // unknown event names are ignored
    }
  };

  const drain = (): boolean => {
    // A CR at the very end may be the first half of a CRLF split between reads: keep it raw for now.
    const heldCr = buf.endsWith('\r');
    buf = (heldCr ? buf.slice(0, -1) : buf).replace(/\r\n?/g, '\n');
    let idx;
    while ((idx = buf.indexOf('\n\n')) !== -1) {
      const block = buf.slice(0, idx);
      buf = buf.slice(idx + 2);
      if (handle(block)) return true;
    }
    if (heldCr) buf += '\r';
    return false;
  };

  try {
    for (;;) {
      const { value, done } = await reader.read();
      if (done) break;
      buf += decoder.decode(value, { stream: true });
      if (drain()) {
        finished = true;
        break;
      }
    }
  } catch (err) {
    if (!isAbort(err, p.signal)) p.onError('network');
    return;
  }

  if (finished) {
    reader.cancel().catch(() => undefined);
    return;
  }
  // Ended with neither done nor error (a frame without its closing blank line is not an event).
  if (p.signal?.aborted) return;
  p.onError('network');
}
